import { Contact, CustomProductDraft, OrganizationTableItem, Product } from 'types'
import SvgIconComponent from '../../images/SvgIconComponent'
import { housingTypeOptions, typeOptions } from '../CreateOrganization/CreateOrganization'
import AffiliatedAutocomplete from '../CustomCompanyComponents/AffiliatedAutocomplete'
import ContractedByAutocomplete from '../CustomCompanyComponents/ContractedByAutocomplete'
import EditTypeSelect from '../EditModeComponents/EditTypeSelect'
import CustomDatePicker from '../shared/DatePicker/CustomDatePicker'
import AverageTenureComponent from './AverageTenureComponent'
import OrgAdditionalContactsAutocomplete from './OrgAdditionalContactsAutocomplete'
import OrgBillingContactAutocomplete from './OrgBillingContactAutocomplete'
import OrgPrimaryContactAutocomplete from './OrgPrimaryContactAutocomplete'
import SelectMargins from './SelectMargins'
import StateAutocomplete from './StateAutocomplete'
import TeamRoleAutocomplete from './TeamRoleAutocomplete'
import UserAutocomplete from './UserAutocomplete'

// Team role configuration
const TEAM_ROLES = [
  { key: 'AM', label: 'Account Manager (AM)' },
  { key: 'PM', label: 'Project Manager (PM)' },
  { key: 'DMS', label: 'Digital Marketing Specialist (DMS)' },
  { key: 'SEO', label: 'SEO Specialist' },
  { key: 'MIA', label: 'Marketing Integration (MIA)' },
  { key: 'BDE', label: 'Business Development (BDE)' },
  { key: 'QA', label: 'Quality Assurance (QA)' },
  { key: 'other', label: 'Other Team Members' },
] as const

export const fieldsConfig = organization => [
  {
    name: 'owner',
    label: 'Owner',
    type: 'component',
    component: UserAutocomplete,
    componentProps: {
      userId: Array.isArray(organization?.owner) ? organization.owner : [organization?.owner],
      label: '',
      context: 'owner',
    },
  },
  { key: 'businessPhone', name: 'businessPhone', label: 'Business Phone', type: 'text' },
  { key: 'billingPhone', name: 'billingPhone', label: 'Billing Phone', type: 'text' },
  { key: 'email', name: 'email', label: 'Billing Email', type: 'text', disabled: true },
  { key: 'name', name: 'name', label: 'Organization Name', type: 'text' },
  { key: 'dba', name: 'dba', label: 'Legal Entity Name', type: 'text' },
  { key: 'website', name: 'website', label: 'Website', type: 'text', showAdornment: true },
  { key: 'cmsUsed', name: 'cmsUsed', label: 'CMS Used', type: 'text', showAdornment: true },
  { key: 'geoTargeting', name: 'geoTargeting', label: 'Geographic Targeting', type: 'text', showAdornment: true },
  {
    key: 'marketingObjective',
    name: 'marketingObjective',
    label: 'Marketing Objective',
    type: 'text',
    showAdornment: true,
  },
  {
    key: 'productsAndServices',
    name: 'productsAndServices',
    label: 'Product & Services to Focus On',
    type: 'text',
    showAdornment: true,
  },
  {
    key: 'facebookAccess',
    name: 'facebookAccess',
    label: 'Facebook Access',
    type: 'text',
    conditional: !organization.website,
  },
  { key: 'summary', name: 'summary', label: 'Summary', type: 'text', multiline: true },
  { key: 'street', name: 'street', label: 'Street', type: 'text', showAdornment: true },
  { key: 'city', name: 'city', label: 'City', type: 'text', showAdornment: true },
  { key: 'state', name: 'state', label: 'State', type: 'text', showAdornment: true },
  { key: 'zip', name: 'zip', label: 'Zip', type: 'text', showAdornment: true },
  { key: 'socialProfiles', name: 'socialProfiles', label: 'Social', type: 'text', showAdornment: true },

  {
    name: 'type',
    label: 'Type',
    type: 'component',
    component: EditTypeSelect,
    componentProps: {
      options: organization.vertical?.some(v => ['Student', 'Multifamily', 'Senior'].includes(v))
        ? housingTypeOptions
        : typeOptions,
      hasDeals: organization.dealIds?.length > 0,
    },
  },
]
export const fieldsConfigSections = (
  organization: OrganizationTableItem,
  organizations: OrganizationTableItem[],
  contacts: Contact[],
  handleChange: (fieldName: string, newValue: any) => void,
  mergedCustomDrafts: CustomProductDraft[],
  handleAddCustomProduct: (catalogProduct: any) => void,
  handleEditCustomDraft: (id: string, patch: Partial<CustomProductDraft>) => void,
  handleRemoveCustomDraft: (id: string) => void,
  selectedProduct: Product,
  selectCustomProduct: (newValue: Product) => void
) => {
  const ownerValue = organization?.owner
  const ownerArray = Array.isArray(ownerValue) ? ownerValue : ownerValue ? [ownerValue] : []
  const sections = [
    organization?.isParent
      ? {
          sectionLabel: 'Custom Service Selection',
          fields: [
            {
              ...(organization?.isParent && {
                name: 'custom_selection',
                label: '',
                type: 'component',
                component: SelectMargins,
                componentProps: {
                  drafts: mergedCustomDrafts,
                  onAddDraft: handleAddCustomProduct,
                  onEditDraft: handleEditCustomDraft,
                  onRemoveDraft: handleRemoveCustomDraft,
                  selectedProduct: selectedProduct,
                  selectCustomProduct: selectCustomProduct,
                },
                showAdornment: true,
              }),
            },
          ],
        }
      : null,
    {
      sectionLabel: 'Information',
      fields: [
        {
          name: 'owner',
          label: 'Owner',
          type: 'component',
          component: UserAutocomplete,
          componentProps: {
            userId: ownerArray,
            label: '',
            context: 'owner',
          },
        },
        { key: 'name', name: 'name', label: 'Organization Name', type: 'text' },
        { key: 'dba', name: 'dba', label: 'Legal Entity Name', type: 'text' },
        { key: 'website', name: 'website', label: 'Website', type: 'text', showAdornment: true },
        {
          name: 'primaryContact',
          label: 'Primary Contact',
          type: 'component',
          component: OrgPrimaryContactAutocomplete,
          componentProps: {
            context: 'primaryContact',
            options: contacts ?? [],
            onBlur: () => {},
          },
        },
        {
          name: 'billingContact',
          label: 'Billing Contact',
          type: 'component',
          component: OrgBillingContactAutocomplete,
          componentProps: {
            context: 'billingContact',
            options: contacts ?? [],
            onSetEmail: handleChange,
            onBlur: () => {},
          },
        },
        {
          name: 'contacts',
          label: 'Addtl Contacts',
          type: 'component',
          component: OrgAdditionalContactsAutocomplete,
          componentProps: {
            context: 'contacts',
            options: contacts ?? [],
            onBlur: () => {},
          },
        },

        {
          name: 'contractedBy',
          label: 'Contracted By',
          type: 'component',
          component: ContractedByAutocomplete,
          componentProps: {
            options: organizations ?? [],
            context: 'contractedBy',
            type: organization?.type ?? '',
            onBlur: () => {},
          },
        },
        {
          name: 'affiliated',
          label: 'Affiliate',
          type: 'component',
          component: AffiliatedAutocomplete,
          componentProps: {
            context: 'affiliated',
            options: organizations ?? [],
            onBlur: () => {},
          },
        },
        {
          name: 'social',
          label: 'Active Social Profiles',
          type: 'component',
          component: SvgIconComponent,
          componentProps: {
            context: 'social',
            socialProfiles: organization && Array.isArray(organization.social) ? organization.social : [],
            onBlur: () => {},
          },
          showAdornment: true,
        },
        {
          name: 'clientStartDate',
          label: 'Client Start Date',
          type: 'component',
          component: CustomDatePicker,
          componentProps: {
            objectData: organization,
            label: 'Client Start Date',
            dateField: 'clientStartDate',
          },
          conditional: !['Cancelled', 'Inactive / Paused']?.includes(organization?.status),
          showAdornment: true,
        },
        {
          // THIS SHOULD BE “averageTenure” not “clientStartDate”
          name: 'averageTenure',
          label: 'Tenure',
          type: 'component',
          component: AverageTenureComponent,
          componentProps: {
            objectData: organization,
            label: 'Average Tenure',
            dateField: 'averageTenure',
          },
          // your conditional…
          conditional: ['Cancelled', 'Inactive / Paused'].includes(organization?.status),
          showAdornment: true,
        },
      ],
    },
    {
      sectionLabel: 'Team',
      fields: TEAM_ROLES.map(({ key, label }) => ({
        name: `team.${key}`,
        label,
        type: 'component',
        component: TeamRoleAutocomplete,
        componentProps: {
          role: key,
          fieldName: `team.${key}`,
        },
      })),
    },
    !organization?.isParent
      ? {
          sectionLabel: 'Contact Info',
          fields: [
            { key: 'businessPhone', name: 'businessPhone', label: 'Business Phone', type: 'text' },
            { key: 'billingPhone', name: 'billingPhone', label: 'Billing Phone', type: 'text' },
            { key: 'email', name: 'email', label: 'Billing Email', type: 'text', disabled: true },
            { key: 'street', name: 'street', label: 'Street', type: 'text', showAdornment: true },
            { key: 'city', name: 'city', label: 'City', type: 'text', showAdornment: true },
            { key: 'state', name: 'state', label: 'State', type: 'component', component: StateAutocomplete },
            { key: 'zip', name: 'zip', label: 'Zip', type: 'text', showAdornment: true },
            { key: 'cmsUsed', name: 'cmsUsed', label: 'CMS Used', type: 'text', showAdornment: true },
            { key: 'socialProfiles', name: 'socialProfiles', label: 'Social', type: 'text', showAdornment: true },
            {
              key: 'marketingObjective',
              name: 'marketingObjective',
              label: 'Marketing Objective',
              type: 'text',
              showAdornment: true,
            },
            {
              key: 'productsAndServices',
              name: 'productsAndServices',
              label: 'Product & Services to Focus On',
              type: 'text',
              showAdornment: true,
            },
            {
              key: 'geoTargeting',
              name: 'geoTargeting',
              label: 'Geographic Targeting',
              type: 'text',
              showAdornment: true,
            },
            {
              key: 'facebookAccess',
              name: 'facebookAccess',
              label: 'Facebook Access',
              type: 'text',
              conditional: !organization?.website,
            },
          ],
        }
      : null,
  ]
  return sections.filter(
    (s): s is { sectionLabel: string; fields: any[] } =>
      !!s && !!s.sectionLabel && Array.isArray(s.fields) && s.fields.length > 0
  )
}
